import { IconTrash } from '@tabler/icons-react';
import type React from 'react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { formatErrorMessage } from '@/utils/error-formatting';
import {
  type ApiRowType,
  type EntityConfig,
  referenceAdminEntityName,
} from './reference-admin-types';

interface DeleteReferenceAdminDialogProps {
  config: EntityConfig;
  entity: ApiRowType;
  onSuccess?: (() => void) | undefined;
}

export const DeleteReferenceAdminDialog: React.FC<DeleteReferenceAdminDialogProps> = ({
  config,
  entity,
  onSuccess,
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get display name for the entity (usually name and code)
  const displayName =
    entity.name && entity.code
      ? `"${entity.name}" (${entity.code})`
      : entity.name || entity.code || 'this item';

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await config.deleteFn(parseInt(entity.id));
      setOpen(false);
      onSuccess?.();
    } catch (_error) {
      const errorMessage = formatErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setError(null);
    }
    setOpen(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          title={`Delete ${config.singularName.toLowerCase()}`}
          aria-label={`Delete ${config.singularName.toLowerCase()} ${referenceAdminEntityName(entity)}`}
        >
          <IconTrash className="h-4 w-4 text-destructive" aria-hidden />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[550px]">
        <DialogHeader>
          <DialogTitle>Delete {config.singularName}</DialogTitle>
          <DialogDescription>
            Are you sure you want to delete the {config.singularName.toLowerCase()} {displayName}?
            This action cannot be undone and may affect related records.
          </DialogDescription>
        </DialogHeader>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null);
              setOpen(false);
            }}
          >
            Cancel
          </Button>
          <Button type="button" variant="destructive" onClick={handleDelete} disabled={isLoading}>
            {isLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
